/**
 * Generate a table of contents from article headings
 * Adds slug-style IDs to h2s in .article-body and creates links in .article-sidebar .wp-block-list
 *
 * @since 1.0.0
 */

/**
 * Convert text to a URL-friendly slug
 *
 * @param {string} text - The text to convert
 * @returns {string} The slug
 */
function textToSlug(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
}

/**
 * Initialize article table of contents
 */
export function initArticleTOC() {
    const articleBodies = document.querySelectorAll('.article-body');
    const sidebarList = document.querySelector('.article-sidebar .wp-block-list');
    const sidebarJump = document.querySelector('.article-sidebar-jump');

    // Exit early if no article bodies or sidebar list
    if (articleBodies.length === 0 || !sidebarList) {
        return;
    }

    // Collect all headings and summary elements across all article bodies
    const allHeadings = [];

    articleBodies.forEach((articleBody) => {
        // Duplicate .article-body-summary above .article-sidebar-jump if it exists
        const sidebarJump = document.querySelector('.article-sidebar-jump');
        const bodySummary = articleBody.querySelector('.article-body-summary');
        if (
            bodySummary &&
            sidebarJump &&
            !sidebarJump.previousElementSibling?.classList.contains('article-body-summary')
        ) {
            const summaryCopy = bodySummary.cloneNode(true);
            sidebarJump.parentNode.insertBefore(summaryCopy, sidebarJump);
        }

        // Find all h2s in this article body
        const headings = articleBody.querySelectorAll('h2');

        // Add IDs to headings and collect them
        headings.forEach((heading) => {
            // Skip h2s inside .article-body-summary
            if (heading.closest('.article-body-summary')) {
                return;
            }

            // Skip h2s marked to exclude from TOC
            if (heading.classList.contains('no-toc')) {
                return;
            }

            // Generate ID if it doesn't already exist
            if (!heading.id) {
                const slug = textToSlug(heading.textContent);
                heading.id = slug;
            }

            // Collect heading info
            allHeadings.push(heading);
        });
    });

    // Exit early if no valid headings found
    if (allHeadings.length === 0) {
        return;
    }

    // Create TOC items for all headings
    const firstExistingItem = sidebarList.querySelector('li');
    let isScrollingFromClick = false;
    let isFirstItem = true;

    allHeadings.forEach((heading) => {
        const listItem = document.createElement('li');
        const link = document.createElement('a');

        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();

        listItem.appendChild(link);

        // Add class to first TOC item
        if (isFirstItem) {
            listItem.classList.add('is-first');
            isFirstItem = false;
        }

        // Add click handler to update active state immediately when clicked
        link.addEventListener('click', () => {
            sidebarList.querySelectorAll('li').forEach((li) => {
                li.classList.remove('is-active');
            });
            listItem.classList.add('is-active');

            // Prevent scroll listener from interfering during smooth scroll animation
            isScrollingFromClick = true;
        });

        // Insert before the first existing item, or append if none exist
        if (firstExistingItem) {
            sidebarList.insertBefore(listItem, firstExistingItem);
        } else {
            sidebarList.appendChild(listItem);
        }
    });

    // Set up scroll listener to track which heading is in view
    // The active heading is the one closest to the 64px mark (has scrolled past but closest to threshold)
    const updateActiveState = () => {
        let activeId = null;
        let smallestDistance = Infinity;
        const resourcesSection = document.querySelector('#resources');

        allHeadings.forEach((heading) => {
            const rect = heading.getBoundingClientRect();
            // We want the heading that has scrolled past the 64px threshold and is closest to it
            if (rect.top <= 64) {
                const distance = 64 - rect.top;
                if (distance < smallestDistance) {
                    smallestDistance = distance;
                    activeId = heading.id;
                }
            }
        });

        // Check if resources section should be active
        if (resourcesSection) {
            const resourcesRect = resourcesSection.getBoundingClientRect();
            if (resourcesRect.top <= 64) {
                const distance = 64 - resourcesRect.top;
                // Only set as active if no heading is closer, or if this is much closer
                if (distance < smallestDistance) {
                    activeId = 'resources';
                }
            }
        }

        // Remove active class from all items
        sidebarList.querySelectorAll('li').forEach((item) => {
            item.classList.remove('is-active');
        });

        // Add active class to matching item if one was found
        if (activeId) {
            const activeLink = sidebarList.querySelector(`a[href="#${activeId}"]`);
            if (activeLink) {
                activeLink.closest('li').classList.add('is-active');
            }
        }
    };

    // Remove resources list item if resources section doesn't exist
    const resourcesSection = document.querySelector('#resources');
    if (!resourcesSection) {
        const resourcesLink = sidebarList.querySelector('a[href="#resources"]');
        if (resourcesLink) {
            resourcesLink.closest('li').remove();
        }
    } else {
        const resourcesLink = sidebarList.querySelector('a[href="#resources"]');
        if (resourcesLink) {
            resourcesLink.closest('li').classList.add('has-resources');
        }
    }

    // Listen for scroll events
    window.addEventListener('scroll', () => {
        if (!isScrollingFromClick) {
            updateActiveState();
        }
    });

    // Initial state on page load
    updateActiveState();

    // Re-enable updates once smooth scroll animation completes
    window.addEventListener('scrollend', () => {
        isScrollingFromClick = false;
        updateActiveState();
    });

    // Handle sticky positioning for .article-sidebar-jump with fixed positioning
    if (sidebarJump) {
        // Determine which fade target element exists, in order of priority. The
        // fade target marks where the sidebar fades out permanently (the
        // article's closing sections).
        const fadeTargetSelectors = ['#resources', '.cta-full', '.cards-related'];
        let fadeTargetElement = null;

        for (const selector of fadeTargetSelectors) {
            const element = document.querySelector(selector);
            if (element) {
                fadeTargetElement = element;
                fadeTargetElement.classList.add('is-fade-target');
                break;
            }
        }

        // Collect the full-width sections that interrupt the article body before
        // the fade target. As the sticky sidebar overlaps one of these it should
        // fade out on approach and fade back in once the section has scrolled
        // past, instead of abruptly disappearing behind it.
        const entryContent = sidebarJump.closest('.entry-content');
        let passThroughSections = [];

        if (entryContent) {
            const directChildren = Array.from(entryContent.children);
            const fadeTargetIndex = fadeTargetElement
                ? directChildren.indexOf(fadeTargetElement)
                : directChildren.length;

            passThroughSections = directChildren.filter((child, index) => {
                return (
                    child.classList.contains('alignfull') &&
                    !child.classList.contains('hero-split') &&
                    (fadeTargetIndex === -1 || index < fadeTargetIndex)
                );
            });
        }

        // Insert a sentinel placeholder that stays in document flow. Once the
        // jump element becomes position: fixed, its own getBoundingClientRect is
        // locked to the viewport, so we measure the sentinel instead to know when
        // to pin/unpin. The placeholder also reserves space to prevent the layout
        // from jumping when the element leaves the flow.
        const placeholder = document.createElement('div');
        placeholder.className = 'article-sidebar-jump-placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        sidebarJump.parentNode.insertBefore(placeholder, sidebarJump);

        // Calculate and update position when window resizes or scrolls
        const updatePosition = () => {
            // Only apply fixed positioning on viewports 992px and wider
            if (window.innerWidth < 992) {
                sidebarJump.classList.remove('is-sticky');
                sidebarJump.style.opacity = '1';
                placeholder.style.height = '0px';
                return;
            }

            // Pin when the element's box edge reaches the 2rem mark. is-sticky
            // fixes top at 2rem, matching the box's current position so there's no
            // positional jump. The padding-top then transitions to 0, smoothly
            // settling the content upward.
            const pinThreshold = 20;

            // Measure the sentinel, which always reflects the natural scroll position
            const sentinelTop = placeholder.getBoundingClientRect().top;
            const isCurrentlySticky = sidebarJump.classList.contains('is-sticky');

            if (!isCurrentlySticky && sentinelTop <= pinThreshold) {
                // Reserve space before pinning so content below doesn't jump up
                placeholder.style.height = sidebarJump.offsetHeight + 'px';
                sidebarJump.classList.add('is-sticky');
            } else if (isCurrentlySticky && sentinelTop > pinThreshold) {
                // Unpin once the sentinel scrolls back below the threshold
                sidebarJump.classList.remove('is-sticky');
                placeholder.style.height = '0px';
            }

            // The fade only matters while the sidebar is pinned and floating over
            // the content. In normal document flow it scrolls with the article and
            // never overlaps anything, so keep it fully visible. This also avoids
            // hiding the sidebar on initial load when a full-width section happens
            // to sit close beneath it.
            if (!sidebarJump.classList.contains('is-sticky')) {
                sidebarJump.style.opacity = '1';
                return;
            }

            // While smooth-scrolling from a TOC click, keep the sidebar fully
            // visible so the jumped-to item isn't left half-faded when it lands
            // near a fade zone (e.g. a short final section next to the footer).
            // The fade re-engages naturally once the user scrolls manually.
            if (isScrollingFromClick) {
                sidebarJump.style.opacity = '1';
                return;
            }

            // Fade the sidebar out as it overlaps full-width sections or the
            // final fade target, then back in once a pass-through section clears.
            const fadeDistance = 200;
            const jumpRect = sidebarJump.getBoundingClientRect();
            let opacity = 1;

            // Pass-through full-width sections: fade out on approach and fade
            // back in after the section scrolls past the sidebar.
            passThroughSections.forEach((section) => {
                const sectionRect = section.getBoundingClientRect();
                const gapBelow = sectionRect.top - jumpRect.bottom; // section below sidebar
                const gapAbove = jumpRect.top - sectionRect.bottom; // section above sidebar
                let sectionOpacity;

                if (gapBelow >= 0) {
                    // Section is still below: fade out as it approaches
                    sectionOpacity =
                        gapBelow >= fadeDistance ? 1 : gapBelow / fadeDistance;
                } else if (gapAbove >= 0) {
                    // Section has scrolled above: fade back in as it clears
                    sectionOpacity =
                        gapAbove >= fadeDistance ? 1 : gapAbove / fadeDistance;
                } else {
                    // Section currently overlaps the sidebar: fully hidden
                    sectionOpacity = 0;
                }

                opacity = Math.min(opacity, sectionOpacity);
            });

            // Final fade target: fade out on approach and stay hidden past it.
            if (fadeTargetElement) {
                const targetRect = fadeTargetElement.getBoundingClientRect();
                const distanceToTarget = targetRect.top - jumpRect.bottom;
                let targetOpacity;

                if (distanceToTarget >= fadeDistance) {
                    // Target is still far below: fully visible
                    targetOpacity = 1;
                } else if (distanceToTarget > 0) {
                    // Within the fade band: fade proportionally as we approach
                    targetOpacity = distanceToTarget / fadeDistance;
                } else {
                    // Element bottom has reached/passed the target: stay hidden
                    targetOpacity = 0;
                }

                opacity = Math.min(opacity, targetOpacity);
            }

            sidebarJump.style.opacity = opacity;
        };

        window.addEventListener('scroll', updatePosition);
        window.addEventListener('resize', updatePosition);
        updatePosition();
    }
}
